<template lang="pug">
  #home
    #board.px-4.px-md-5.pt-5
      .row.title.d-flex.mx-0.mt-md-5.mb-5
        h1.light-font PLATAFORMA
        h1.bold-font CIUDADANA DE FOSAS
      .row.intro.subtitle.d-flex.mx-0.my-5.pt-md-5.px-md-5
        p.mb-0.bold-font Introducción
      .row.intro-text.d-flex.light-font.mx-0.mt-3.px-md-5
        p.mb-4
          | México vive una grave crisis de derechos humanos con más de 130 mil personas desaparecidas y no localizadas registradas hasta septiembre de 2024. A pesar de la magnitud del fenómeno de las desapariciones y de la crisis forense, los datos del gobierno mexicano son escasos, poco accesibles y con serios problemas de trazabilidad para identificar modificaciones a lo largo del tiempo. Si bien hay un
          a(
            href="https://versionpublicarnpdno.segob.gob.mx/Dashboard/Index"
            target="_blank"
          )  Registro Nacional de Personas Desaparecidas y No Localizadas (RNPDNO)
          |  que actualmente se está actualizando; los nombres, las fotografías, las señas particulares y otros datos relevantes para la localización no están disponibles.
        p.mb-4 Es habitual que las tareas de búsqueda recaigan en las familias de las personas desaparecidas, aunque por ley corresponde al Estado encontrarlas. Las familias se movilizan para elaborar fichas con datos básicos, pegar carteles y publicar la información referente a sus seres queridos en redes sociales. Asimismo, son ellas quienes empujan leyes locales y nacionales en la materia, o elaboran sus propias bases de datos y sus informes. Las familias, mayoritariamente mujeres, madres, hermanas e hijas, son quienes indagan sobre el paradero de sus seres queridos y siguen el rastro de sus huellas, poniendo en riesgo su integridad física y psicoemocional.
        p.mb-4 En esas búsquedas, los hallazgos de fosas clandestinas han sido recurrentes y extendidos en la mayoría de las entidades de México desde el año 2007, como consecuencia de la militarización de la seguridad pública que inició en el sexenio de Felipe Calderón, y que se ha intensificado hasta la administración más reciente con Claudia Sheinbaum. El fenómeno refleja el grado de descomposición social y deshumanización alcanzado como producto de una política de drogas punitiva y no preventiva, el uso de la fuerza militar y un sistema de procuración de justicia incapaz de investigar y perseguir eficazmente a los responsables de graves violaciones a los derechos humanos.
        p.mb-4
          | En
          a(
            href="https://articulo19.org/lanzamiento-de-la-plataforma-ciudadana-de-fosas-clandestinas-por-parte-de-article-19-cmdpdh-ibero-data-civica-y-hrdag"
            target="_blank"
          )  septiembre de 2021
          |, esta Plataforma Ciudadana de Fosas nació como un ejercicio ciudadano ante la falta de una versión pública del Registro Nacional de Fosas Comunes y de Fosas Clandestinas que – según la
          a(
            href="https://www.diputados.gob.mx/LeyesBiblio/pdf/LGMDFP.pdf"
            target="_blank"
          )  Ley General en Materia de Desaparición Forzada de Personas, Desaparición Cometida por Particulares y del Sistema Nacional de Búsqueda de Personas
          |  – es responsabilidad de la Fiscalía General de la República (FGR) en colaboración con las fiscalías y procuradurías estatales.
        p.mb-4
          | En marzo de 2023, la Comisión Nacional de Búsqueda (CNB) hizo público el
          span.bold-font  Mapa de Hallazgos de Fosas Clandestinas
          |  con datos solicitados por dicha institución a la FGR y las fiscalías estatales. En este mapa, actualizado hasta 2024, la persona usuaria podía conocer el número de fosas por estado y por municipio. Sin embargo, la herramienta no daba cuenta del número de cuerpos y/o fragmentos humanos recuperados. Tampoco brindaba la información de todas las fiscalías, incluyendo solo de aquellas que deciden transparentar. Aunque fue un avance significativo, actualmente el sitio no está activo.
        p.mb-4 En el año 2024 la CNB quitó su registro público con datos de fosas clandestinas, cuya información disponible era hasta agosto de 2023 y con información de menos de la mitad de las entidades federativas. Es decir, solo con datos de 15 de las 32 entidades en México. Hoy no se cuenta con un registro oficial público sobre fosas clandestinas por esta institución.
        p.mb-4 Contrario al esfuerzo inicial de la CNB, la Plataforma Ciudadana de Fosas registra hallazgos en todos los estados del país, siendo una herramienta de información ante ese vacío por parte de las autoridades. Pero sobre todo, esta plataforma es una herramienta dirigida a las cientos de familias y colectivos de búsqueda en México, así como a la sociedad en general.
        p.mb-4 La Plataforma reúne documentación de los hallazgos de fosas clandestinas en México reportadas por 1) prensa internacional, nacional y local; 2) fiscalías, procuradurías locales y servicios forenses, como el caso de Jalisco; y 3) Fiscalía General de la República (FGR), estas últimas obtenidas vía Transparencia y Acceso a la Información Pública. En resumen, aquí encontrarás los datos de fosas clandestinas de fuentes oficiales y de fuentes abiertas desde el año 2006 hasta diciembre de 2024.
        p.mb-4
          | Esta herramienta ciudadana muestra el número de fosas clandestinas por entidad y por municipio; el número de cuerpos, de restos y/o fragmentos óseos de personas desaparecidas y, en los casos que logramos obtener la información, el sexo de las víctimas. En la pestaña de
          span.bold-font  Repositorio
          |  podrás descargar las solicitudes de información y las respuestas de las instituciones públicas, donde también se evidencian los retos que enfrenta la crisis forense y de desaparición en México en materia de transparencia.
        p.mb-4 El procesamiento de la información implicó un reto debido a la diferencia en el número de fosas reportadas por los distintos sujetos obligados, lo cual resulta preocupante porque muestra que la sociedad sigue sin tener certeza de la magnitud del fenómeno de fosas clandestinas en México. Las discrepancias entre fuentes oficiales es un indicio de la falta de interoperabilidad entre instituciones, cuando estas deberían trabajar de manera conjunta en la búsqueda de personas desaparecidas.
        p.mb-4 En el caso de las fuentes abiertas, los datos obtenidos de la prensa tienen sus limitantes porque no pueden dar cuenta del total de hallazgos de fosas; sin embargo, resultan útiles para contrastar y evaluar los datos oficiales. Ya que, sabemos, algunas de las fosas clandestinas son documentadas por la prensa, pero no todas son registradas por las instituciones.
        p.mb-4
          | La última actualización de la Plataforma Ciudadana de Fosas contiene una sección de diagnóstico sobre el abandono forense en México y la experiencia de documentación en fosas comunes del colectivo
          span.bold-font  Regresando a Casa Morelos
          |  a partir de 2017. Estas, a diferencia de las fosas clandestinas, son espacios formales, generalmente ubicados en panteones municipales o ministeriales y bajo resguardo del Estado, destinados legalmente al entierro de cuerpos no identificados o no reclamados.
        p.mb-4
          | Para la construcción de este diagnóstico se realizaron entrevistas a colectivos de Coahuila, Jalisco, Morelos, Ciudad de México, Chihuahua y Guanajuato, dedicados a la búsqueda de personas en fosas comunes, así como sus experiencias en Semefos y fosas comunes en panteones al resguardo del Estado mexicano. Además del documento disponible para descarga en la sección
          span.bold-font  Diagnóstico Forense
          |, podrás encontrar 19 tarjetas de identificación forense ilustradas por el colectivo
          span.bold-font  Regresando a Casa Morelos
          |  durante el proceso de exhumación de cuerpos de las fosas comunes de Tetelcingo y Jojutla.
        p.mb-4 Estas prácticas de documentación de las familias buscadoras surgen en respuesta frente a la negligencia del Estado durante los procesos de exhumación, identificación y resguardo de cuerpos. El reemplazo del registro fotográfico formal por anotaciones detalladas, escucha atenta y dibujos, constituyen una memoria visual y documental fundamental frente a la crisis forense y de desaparición en México.
        p.mb-4 En este contexto, resulta fundamental distinguir entre fosas clandestinas y fosas comunes, ya que si bien ambas están vinculadas a la desaparición de personas y a la crisis forense, responden a lógicas y responsabilidades estatales distintas. Este estudio recupera la definición de fosa clandestina propuesta por el Programa de Derechos Humanos de la Universidad Iberoamericana, Ciudad de México, como un “sitio donde uno o más cuerpos o restos de personas fueron enterrados o semienterrados, de forma anónima o ilegal, en el intento de ocultar o destruir evidencia, y que posteriormente fueron exhumados por particulares o autoridades”.
        p.mb-4 De acuerdo con la metodología empleada, una fosa clandestina se caracteriza por: (1) contener uno o más cuerpos o restos humanos; (2) encontrarse enterrados o semienterrados; (3) presentar dudas sobre la legalidad del sitio de hallazgo; y (4) ser descubierta por particulares o autoridades. En contraste, las fosas comunes son espacios formales, generalmente ubicados en panteones municipales o ministeriales y bajo resguardo del Estado, destinados legalmente al entierro de cuerpos no identificados o no reclamados.
        p.mb-4 Nuestro país no tiene un registro público confiable de fosas clandestinas, mucho menos uno de fosas comunes, donde también podrían localizar a personas reportadas como desaparecidas. A esto se suma la opacidad estatal sobre el destino de los cuerpos recuperados, su resguardo y su identificación forense. En conjunto, estas carencias institucionales no sólo dificultan la localización e identificación de personas desaparecidas, sino que también obstaculizan el acceso a la verdad, la justicia y la reparación para las víctimas y sus familias.

    .video.mb-5
      iframe(
        :width="videoWidth"
        :height="videoHeight"
        src="https://www.youtube.com/embed/zAZz4XwElME"
        title="Conoce la nueva Plataforma Ciudadana de Fosas Clandestinas"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      )
    .video.mb-5
      iframe(
        :width="videoWidth"
        :height="videoHeight"
        src="https://www.youtube.com/embed/7jC6MgYijR8"
        title="Conoce la nueva Plataforma Ciudadana de Fosas Clandestinas"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      )
    .video
      iframe(
        :width="videoWidth"
        :height="videoHeight"
        src="https://www.youtube.com/embed/xkrlc4_El7Y"
        title="Violencia y Terror. Hallazgos sobre Fosas Clandestinas en México 2006-2017"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      )
    #instructions
      .row.instructions.subtitle.d-flex.mx-0.px-5.my-5.pb-md-4
        p.mb-0.bold-font ¿Cómo funciona la plataforma?
      .row.instructions-text.d-flex.mx-0.mt-5.pb-5.px-md-5
        .col-12.col-md-6.instructions-container
          .instruction.px-3.px-md-5.py-4
            .row.image.d-flex.mx-0
              .col-2
                span 1
              .col-10.img-container
                img.img-fluid(src="@/assets/instructions/Paso 1.png")
            .row.text.mx-0.pt-4
              p.mb-0 Usa el selector de fuentes para conocer las fosas que una o más fuentes han
                |  reportado.
                span  (Recuerda que una misma fosa puede ser reportada por dos fuentes o más)
        .col-12.col-md-6.instructions-container
          .instruction.px-3.px-md-5.py-4
            .row.image.d-flex.mx-0
              .col-2
                span 2
              .col-10.img-container
                img.img-fluid(src="@/assets/instructions/Paso 2.png")
            .row.text.mx-0.pt-4
              p.mb-0 Consulta toda la información disponible o filtra los resultados según los
                |  años de hallazgo reportados.
        .col-12.col-md-6.instructions-container
          .instruction.px-3.px-md-5.py-4
            .row.image.d-flex.mx-0
              .col-2
                span 3
              .col-10.img-container
                img.img-fluid(src="@/assets/instructions/Paso 3.png")
            .row.text.mx-0.pt-4
              p.mb-0 Busca el estado o municipio de tu interés o da click directamente sobre él
                |  en el mapa.
        .col-12.col-md-6.instructions-container.not-aligned
          .instruction.px-3.px-md-5.py-4
            .row.image.d-flex.mx-0
              .col-2
                span 4
              .col-10.img-container
                .img-container
                  img.img-fluid(src="@/assets/instructions/Paso 4.png")
            .row.text.mx-0.pt-4
              p.mb-0 Consulta las cifras agregadas y desagregadas reportadas por cada autoridad
                |  para el municipio o estado seleccionado.
        .col-12.col-md-6.instructions-container.not-aligned
          .instruction.px-3.px-md-5.py-4
            .row.image.d-flex.mx-0
              .col-2
                span 5
              .col-10.img-container
                img.img-fluid(src="@/assets/instructions/Paso 5.png")
            .row.text.mx-0.pt-4
              p.mb-0 Consulta las estimaciones más recientes para conocer la probabilidad de
                |  encontrar nuevos hallazgos en el municipio seleccionado.
        .col-12.col-md-6.instructions-container.pb-4
          .instruction.px-3.px-md-5.py-4
            .row.image.d-flex.mx-0
              .col-2
                span 6
              .col-10.img-container
                img.img-fluid(src="@/assets/instructions/Paso 6.png")
            .row.text.mx-0.pt-4
              p.mb-0 Para conocer más, consulta nuestra nota metodológica y base de datos oficial,
                |  así como nuestro repositorio digital con los registros y solicitudes de
                |  información que alimentan este proyecto.
      .row.go-to-map.subtitle.d-flex.mx-0.px-5.pb-5
        router-link.mb-0.bold-font(to="/tablero") Ir al Tablero

</template>

<script>
export default {
  name: "Home",
  data() {
    return {
      videoWidth: "500",
      videoHeight: "300"
    };
  },
  computed: {
    mobile() {
      return this.$store.state.mobile;
    }
  },
  mounted() {
    this.videoWidth = this.mobile
      ? "100%"
      : document.querySelector("#video").clientWidth / 2;
    this.videoHeight = document.querySelector("#video").clientHeight;
  }
};
</script>

<style lang="scss">
@import "@/global.scss";
#home {
  #board {
    min-height: 80vh;
    background-image: url("../assets/Fondo_DashboardFosas.png");
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
    .title {
      margin: auto;
      flex-direction: column;
      justify-content: center;
      h1.bold-font {
        color: $black;
        background-color: #e0e0e0;
        width: fit-content;
        margin: auto;
        padding: 0px 5px;
      }
    }
    .intro-text {
      justify-content: center;
      flex-direction: column;
      p {
        margin: auto;
        width: 40em;
      }
    }
  }
  .subtitle {
    justify-content: center;
    font-size: 18px;
  }
  .video {
    min-width: 100%;
    height: auto;
    min-height: 20%;
  }
  #instructions {
    .instructions-container {
      height: fit-content;
    }
    .not-aligned {
      top: -50px;
      span {
        position: absolute;
        top: 25%;
      }
    }
    .instruction {
      align-self: center;
      .image {
        span {
          font-weight: bold;
          font-size: 2.5em;
        }
        .img-container {
          text-align: left;
        }
      }
      .text {
        font-size: 14px;
        text-align: left;
        margin-left: 20% !important;
      }
    }
    .go-to-map a {
      color: white;
    }
  }
}
@media (max-width: 767.98px) {
  #home {
    #board {
      .title h1 {
        font-size: 28px;
      }
      .intro-text p {
        width: auto;
        font-size: 13px;
      }
    }
    .not-aligned {
      top: 0 !important;
      span {
        position: inherit !important;
        top: 0 !important;
      }
    }
  }
}
</style>
